import React, { useState, useEffect, useMemo, useContext } from "react";
import { MdHighlightOff } from "react-icons/md";
import DivLoader from "components/divloader/DivLoader";
import toast from "react-hot-toast";
import { getImage } from "utils/commonFunction";
import { ClientContext } from "ClientProvider";
import {assignGatewayToClient } from "api/graphQL_clients";

const AssignGateways = ({
  id,
  fetchClients,
  assigned_payment_methods,
  infoData,
  refreshParent,
  gatewayRefreshKey,
  paymentMethods,
  allGateways,
  org_id,
  setIsLoading
}: {
  id: string;
  fetchClients: () => void;
  refreshParent: () => void;
  assigned_payment_methods?: any;
  infoData?: any;
  gatewayRefreshKey?: any;
  paymentMethods?: any;
  allGateways? : any;
  org_id?:any;
  setIsLoading:(value:any) => void;
}) => {
    const [isOpenAssignGateway, setIsOpenAssignGateway] = useState(false);
    const [isAssignLoading, setIsAssignLoading] = useState(false);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [searchText, setSearchText] = useState("");
    const { singleRoleData, setSingleRoleData } = useContext(ClientContext);
    
    useEffect(() => {
        if (isOpenAssignGateway) {
        setSelectedIds([]);
        setSearchText("");
        }
    }, [isOpenAssignGateway]);

    const onCloseAssignGateway = () => setIsOpenAssignGateway(false);

    const alreadyAssigned: string[] = useMemo(() => {
        const raw = assigned_payment_methods?.assigned_payment_methods;
        if (!raw) return [];
        if (Array.isArray(raw)) return raw.map(String);
        if (typeof raw === "object") return Object.keys(raw);
        return [];
    }, [assigned_payment_methods]);

    const availableGateways: any[] = useMemo(() => {
        if (!paymentMethods || !Array.isArray(paymentMethods)) return [];
        if (!allGateways  || !Array.isArray(allGateways))  return [];
    
        const unassignedIds = paymentMethods.filter(
        (id: string) => !alreadyAssigned.includes(String(id))
        );
    
        return unassignedIds
        .map((id: string) => allGateways.find((gw: any) => String(gw.id) === String(id)))
        .filter(Boolean);
    }, [paymentMethods, alreadyAssigned, allGateways]);

    const filteredGateways = useMemo(() => {
        if (!searchText.trim()) return availableGateways;
        const q = searchText.toLowerCase();
        return availableGateways.filter(
        (gw: any) =>
            String(gw.name ?? "").toLowerCase().includes(q) ||
            String(gw.id ?? "").toLowerCase().includes(q)
        );
    }, [availableGateways, searchText]);

    const toggleGateway = (gwId: string) => {
        setSelectedIds((prev) =>
        prev.includes(gwId) ? prev.filter((x) => x !== gwId) : [...prev, gwId]
        );
    };

    const handleSelectAll = () => {
        if (selectedIds.length === filteredGateways.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(filteredGateways.map((gw: any) => String(gw.id)));
        }
    };

    const handleAssignGateways = async () => {
        if (selectedIds.length === 0) {
            toast.error("Please select at least one gateway");
            return;
        }
        try {
            setIsLoading(true)
            setIsAssignLoading(true);
            const payload: number[] = [];
            selectedIds.forEach((gid:any) => {
                payload.push(gid);
            });
            alreadyAssigned.forEach((gid:any) => {
                payload.push(gid);
            });
            const update =  await assignGatewayToClient({ client_id: id, org_id:org_id,assigned_payment_methods: payload });

            if (update.errors) toast.error(update.errors[0].message);
            else if (update[0]?.status === "Success") { 
              toast.success("Gateway assigned successfully"); 
              refreshParent(); 
              onCloseAssignGateway(); 
            }
            else toast.error(update[0]?.message ?? "Something went wrong");
            
        } catch (err: any) {
            toast.error(err?.response?.data?.message ?? "Failed to assign gateways");
        } finally {
            // setIsLoading(false)
            setIsAssignLoading(false);
        }
    };

    const allSelected =
        filteredGateways.length > 0 &&
        selectedIds.length === filteredGateways.length;

  return (
    <>
      {/* ── Trigger button ── */}
      <button
        className="wm-add-btn"
        onClick={() => setIsOpenAssignGateway(true)}
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="16" />
          <line x1="8" y1="12" x2="16" y2="12" />
        </svg>
        Assign New Gateway
      </button>

      {/* ── Modal overlay ── */}
      {isOpenAssignGateway && (
        <div
          className="cg-overlay"
          onClick={onCloseAssignGateway}
        >
          <div
            className="cgm-modal"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="cg-modal-hd">
              <div className="cgm-hd-left">
                <div className="cgm-hd-icon">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#fff"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="1" y="4" width="22" height="16" rx="2" />
                    <line x1="1" y1="10" x2="23" y2="10" />
                  </svg>
                </div>
                <div>
                  <h4 className="cgm-hd-title">Assign Gateways</h4>
                  <p className="cgm-hd-sub">
                    {availableGateways.length} available ·{" "}
                    {alreadyAssigned.length} already assigned
                  </p>
                </div>
              </div>
              <button
                onClick={onCloseAssignGateway}
                className="cg-modal-close"
              >
                <MdHighlightOff size={22} />
              </button>
            </div>

            {/* Search + select-all bar */}
            <div className="cgm-toolbar">
              <div className="cgm-search-wrap">
                <svg
                  className="cgm-search-icon"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  type="text"
                  className="cgm-search-input"
                  placeholder="Search gateways…"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
                {searchText && (
                  <button
                    className="cgm-search-clear"
                    onClick={() => setSearchText("")}
                  >
                    ×
                  </button>
                )}
              </div>

              {filteredGateways.length > 0 && (
                <button
                  className={`cgm-select-all${allSelected ? " cgm-select-all--active" : ""}`}
                  onClick={handleSelectAll}
                >
                  <div
                    className={`cg-check-box${allSelected ? " cg-check-box--on" : ""}`}
                  >
                    {allSelected && (
                      <svg
                        width="9"
                        height="9"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </div>
                  {allSelected ? "Deselect all" : "Select all"}
                </button>
              )}
            </div>

            {/* Selected count banner */}
            {selectedIds.length > 0 && (
              <div className="cgm-selected-banner">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                <span>
                  <strong>{selectedIds.length}</strong> gateway
                  {selectedIds.length !== 1 ? "s" : ""} selected
                </span>
                <button
                  className="cgm-clear-btn"
                  onClick={() => setSelectedIds([])}
                >
                  Clear
                </button>
              </div>
            )}

            {/* Gateway grid */}
            <div className="cgm-body">
              {filteredGateways.length === 0 ? (
                <div className="cgm-empty">
                  {availableGateways.length === 0 ? (
                    <>
                      <div className="cgm-empty-icon">
                        <svg
                          width="26"
                          height="26"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <rect x="1" y="4" width="22" height="16" rx="2" />
                          <line x1="1" y1="10" x2="23" y2="10" />
                        </svg>
                      </div>
                      <p className="cgm-empty-title">All gateways assigned</p>
                      <p className="cgm-empty-sub">
                        This client already has all available gateways assigned.
                      </p>
                    </>
                  ) : (
                    <>
                      <div className="cgm-empty-icon">
                        <svg
                          width="22"
                          height="22"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <circle cx="11" cy="11" r="8" />
                          <line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                      </div>
                      <p className="cgm-empty-title">No results</p>
                      <p className="cgm-empty-sub">
                        No gateways match "{searchText}"
                      </p>
                    </>
                  )}
                </div>
              ) : (
                <div className="cgm-grid">
                  {filteredGateways.map((gw: any) => {
                    const gwId = String(gw.id);
                    const isSelected = selectedIds.includes(gwId);
                    return (
                      <div
                        key={gwId}
                        className={`cgm-gw-card${isSelected ? " cgm-gw-card--selected" : ""}`}
                        onClick={() => toggleGateway(gwId)}
                      >
                        {/* Checkbox */}
                        <div
                          className={`cgm-checkbox${isSelected ? " cgm-checkbox--on" : ""}`}
                        >
                          {isSelected && (
                            <svg
                              width="9"
                              height="9"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="3.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          )}
                        </div>
                        {singleRoleData?.[0]?.client?.value?.client_show_method_name && (
                            <div className="cgm-gw-logo-wrap">
                            {gw.name ? (
                                <img
                                src={getImage(gw.name)}
                                alt={gw.name}
                                className="cgm-gw-logo"
                                onError={(e: any) => {
                                    e.target.style.display = "none";
                                    e.target.nextSibling.style.display = "flex";
                                }}
                                />
                            ) : null}
                            <div
                                className="cgm-gw-logo-fallback"
                                style={{ display: gw.name ? "none" : "flex" }}
                            >
                                {String(gw.name ?? "?")
                                .charAt(0)
                                .toUpperCase()}
                            </div>
                            </div>
                        )}

                        <div className="cgm-gw-info">
                          {singleRoleData?.[0]?.client?.value?.client_show_method_name && (  
                            <p className="cgm-gw-name">{gw.name ?? "Unknown"}</p>
                          )}
                          {!singleRoleData?.[0]?.client?.value?.client_show_method_name && (  
                            <p className="cgm-gw-id">
                                {gwId}
                            </p>
                          )}
                          
                        </div>

                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="cg-modal-ft">
              <div className="cgm-ft-left">
                {selectedIds.length > 0 && (
                  <span className="cgm-ft-count">
                    {selectedIds.length} selected
                  </span>
                )}
              </div>
              <div className="cgm-ft-right">
                <button
                  className="cg-cancel-btn"
                  onClick={onCloseAssignGateway}
                >
                  Cancel
                </button>
                <button
                  className="cg-save-btn"
                  onClick={handleAssignGateways}
                  disabled={selectedIds.length === 0 || isAssignLoading}
                >
                  {isAssignLoading ? (
                    <DivLoader className="h-5 w-5 border-white" />
                  ) : (
                    <>
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      Assign{" "}
                      {selectedIds.length > 0 ? `(${selectedIds.length})` : ""}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        /* ── Trigger ── */
        .wm-add-btn {
          display: inline-flex; align-items: center; gap: 7px;
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 13px; font-weight: 700; color: #fff;
          padding: 9px 16px; border-radius: 10px; border: none;
          background: linear-gradient(135deg, #1d4ed8, #2563eb);
          cursor: pointer;
          box-shadow: 0 3px 10px rgba(37,99,235,.28);
          transition: transform .15s, box-shadow .18s;
        }
        .wm-add-btn:hover { transform: translateY(-1px); box-shadow: 0 6px 18px rgba(37,99,235,.36); }

        /* ── Modal shell ── */
        .cgm-modal {
          font-family: 'Plus Jakarta Sans', sans-serif;
          background: #fff;
          border-radius: 18px;
          width: 100%;
          max-width: 580px;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
          box-shadow: 0 24px 64px rgba(15,23,42,.22);
          overflow: hidden;
        }

        /* Header */
        .cg-modal-hd {
          padding: 16px 20px;
          background: linear-gradient(135deg, #0f2744 0%, #1a3d6b 60%, #1d4ed8 100%);
          display: flex; align-items: center; justify-content: space-between;
          flex-shrink: 0;
        }
        .cgm-hd-left { display: flex; align-items: center; gap: 12px; }
        .cgm-hd-icon {
          width: 38px; height: 38px; border-radius: 10px;
          background: rgba(255,255,255,.15); border: 1.5px solid rgba(255,255,255,.25);
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .cgm-hd-title { font-size: 15px; font-weight: 800; color: #fff; margin: 0 0 2px; line-height: 1; }
        .cgm-hd-sub { font-size: 11.5px; color: rgba(255,255,255,.55); font-weight: 500; margin: 0; }
        .cg-modal-close {
          background: rgba(255,255,255,.15); border: none; border-radius: 8px;
          width: 32px; height: 32px; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          color: rgba(255,255,255,.8); transition: background .15s;
        }
        .cg-modal-close:hover { background: rgba(255,255,255,.25); color: #fff; }

        /* Toolbar (search + select all) */
        .cgm-toolbar {
          display: flex; align-items: center; gap: 10px;
          padding: 12px 16px;
          border-bottom: 1px solid #f1f5f9;
          background: #fafbfc;
          flex-shrink: 0;
          flex-wrap: wrap;
        }
        .cgm-search-wrap {
          display: flex; align-items: center; gap: 8px;
          flex: 1; min-width: 180px;
          background: #fff; border: 1.5px solid #e2e8f0; border-radius: 10px;
          padding: 0 12px; height: 38px;
          transition: border-color .15s;
        }
        .cgm-search-wrap:focus-within { border-color: #93c5fd; }
        .cgm-search-icon { color: #94a3b8; flex-shrink: 0; }
        .cgm-search-input {
          flex: 1; border: none; outline: none; background: transparent;
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 13px; color: #0f172a;
        }
        .cgm-search-input::placeholder { color: #cbd5e1; }
        .cgm-search-clear {
          background: none; border: none; cursor: pointer;
          color: #94a3b8; font-size: 18px; line-height: 1;
          padding: 0; display: flex; align-items: center;
          transition: color .12s;
        }
        .cgm-search-clear:hover { color: #64748b; }

        .cgm-select-all {
          display: inline-flex; align-items: center; gap: 7px;
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 12.5px; font-weight: 600; color: #64748b;
          padding: 7px 13px; border-radius: 9px;
          border: 1.5px solid #e2e8f0; background: #fff;
          cursor: pointer; transition: all .12s; white-space: nowrap;
        }
        .cgm-select-all:hover { border-color: #93c5fd; color: #2563eb; }
        .cgm-select-all--active { border-color: #bfdbfe; background: #eff6ff; color: #1d4ed8; }

        /* Selected banner */
        .cgm-selected-banner {
          display: flex; align-items: center; gap: 8px;
          padding: 9px 16px;
          background: linear-gradient(135deg, #eff6ff, #dbeafe);
          border-bottom: 1px solid #bfdbfe;
          font-size: 13px; font-weight: 600; color: #1e40af;
          flex-shrink: 0;
        }
        .cgm-selected-banner strong { font-weight: 800; }
        .cgm-clear-btn {
          margin-left: auto;
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 12px; font-weight: 600; color: #2563eb;
          background: none; border: none; cursor: pointer;
          padding: 2px 8px; border-radius: 6px;
          transition: background .12s;
        }
        .cgm-clear-btn:hover { background: rgba(37,99,235,.08); }

        /* Body */
        .cgm-body {
          flex: 1; overflow-y: auto; padding: 14px 16px;
          min-height: 160px;
        }
        .cgm-body::-webkit-scrollbar { width: 4px; }
        .cgm-body::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 100px; }

        /* Empty state */
        .cgm-empty {
          display: flex; flex-direction: column; align-items: center;
          justify-content: center; gap: 10px; padding: 40px 20px;
        }
        .cgm-empty-icon {
          width: 52px; height: 52px; border-radius: 13px;
          background: #f8fafc; border: 1.5px solid #e2e8f0;
          display: flex; align-items: center; justify-content: center;
          color: #cbd5e1;
        }
        .cgm-empty-title { font-size: 14px; font-weight: 700; color: #64748b; margin: 0; }
        .cgm-empty-sub { font-size: 12.5px; color: #94a3b8; font-weight: 500; margin: 0; text-align: center; }

        /* Gateway grid */
        .cgm-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
          gap: 10px;
        }

        /* Gateway card */
        .cgm-gw-card {
          display: flex; align-items: center; gap: 10px;
          padding: 12px 14px;
          background: #fafbfc; border: 1.5px solid #e2e8f0;
          border-radius: 12px; cursor: pointer;
          transition: all .15s; position: relative;
          user-select: none;
        }
        .cgm-gw-card:hover {
          border-color: #93c5fd; background: #f0f9ff;
        }
        .cgm-gw-card--selected {
          border-color: #2563eb !important;
          background: linear-gradient(135deg, #eff6ff, #dbeafe) !important;
          box-shadow: 0 0 0 3px rgba(37,99,235,.1);
        }

        /* Checkbox */
        .cgm-checkbox {
          width: 18px; height: 18px; border-radius: 5px;
          border: 2px solid #cbd5e1; background: #fff;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0; transition: all .12s; color: #fff;
        }
        .cgm-checkbox--on { background: #2563eb; border-color: #2563eb; }

        /* Logo */
        .cgm-gw-logo-wrap { width: 36px; height: 26px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; }
        .cgm-gw-logo { max-width: 36px; max-height: 26px; object-fit: contain; }
        .cgm-gw-logo-fallback {
          width: 36px; height: 26px; border-radius: 6px;
          background: linear-gradient(135deg, #eff6ff, #dbeafe);
          border: 1px solid #bfdbfe;
          font-size: 13px; font-weight: 800; color: #2563eb;
          align-items: center; justify-content: center;
        }

        /* Info */
        .cgm-gw-info { flex: 1; min-width: 0; }
        .cgm-gw-name {
          font-size: 13px; font-weight: 700; color: #0f172a;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
          margin: 0 0 2px;
        }
        .cgm-gw-id {
          font-size: 11px; color: #94a3b8; font-weight: 500;
          font-family: 'JetBrains Mono', 'Fira Code', monospace;
          margin: 0; white-space: nowrap;
        }

        /* Type badge */
        .cgm-gw-type {
          font-size: 10.5px; font-weight: 700;
          padding: 2px 8px; border-radius: 100px;
          background: #f0fdf4; color: #15803d;
          border: 1px solid #bbf7d0;
          white-space: nowrap; text-transform: capitalize; flex-shrink: 0;
        }

        /* Footer */
        .cg-modal-ft {
          padding: 12px 16px;
          border-top: 1.5px solid #f1f5f9;
          background: #fafbfc;
          display: flex; align-items: center; justify-content: space-between;
          flex-shrink: 0; gap: 10px;
        }
        .cgm-ft-left { display: flex; align-items: center; }
        .cgm-ft-right { display: flex; align-items: center; gap: 10px; }
        .cgm-ft-count {
          font-size: 12.5px; font-weight: 700; color: #2563eb;
          background: #eff6ff; border: 1px solid #bfdbfe;
          padding: 4px 11px; border-radius: 100px;
        }

        .cg-cancel-btn {
          display: inline-flex; align-items: center;
          padding: 9px 16px; border-radius: 10px;
          background: #f1f5f9; border: 1.5px solid #e2e8f0;
          color: #374151; font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 13px; font-weight: 600; cursor: pointer;
          transition: all .12s;
        }
        .cg-cancel-btn:hover { background: #e2e8f0; }

        .cg-save-btn {
          display: inline-flex; align-items: center; gap: 7px;
          padding: 9px 18px; border-radius: 10px;
          background: linear-gradient(135deg, #1d4ed8, #2563eb);
          color: #fff; font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 13.5px; font-weight: 700; border: none;
          cursor: pointer; transition: all .15s;
          box-shadow: 0 2px 8px rgba(37,99,235,.3);
        }
        .cg-save-btn:hover:not(:disabled) {
          background: linear-gradient(135deg, #1e40af, #1d4ed8);
          box-shadow: 0 4px 14px rgba(37,99,235,.4);
        }
        .cg-save-btn:disabled { opacity: .5; cursor: not-allowed; }

        @media (max-width: 520px) {
          .cgm-modal { border-radius: 14px; }
          .cgm-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </>
  );
};

export default AssignGateways;